import { toast } from 'react-toastify'
import { useParams } from 'react-router'
import PropTypes from 'prop-types'
import React, { useState, useEffect, useCallback, useReducer, useMemo } from 'react'

import {
  getFishNameConstants,
  getFishNameOptions,
} from '../../../../App/mermaidData/fishNameHelpers'
import {
  getCollectRecordDataInitialValues,
  getSampleInfoInitialValues,
  getTransectInitialValues,
} from '../collectRecordFormInitialValues'
import { getDataForSubNavNode } from '../../../../library/getDataForSubNavNode'
import { getToastArguments } from '../../../../library/getToastArguments'
import getSelectableAttributes from '../../../../App/mermaidData/getSelectableAttributes'
import { reformatFormValuesIntoFishBeltRecord } from '../reformatFormValuesIntoRecord'
import { sortArrayByObjectKey } from '../../../../library/arrays/sortArrayByObjectKey'
import { useCurrentUser } from '../../../../App/CurrentUserContext'
import { useDatabaseSwitchboardInstance } from '../../../../App/mermaidData/databaseSwitchboard/DatabaseSwitchboardContext'
import { useHttpResponseErrorHandler } from '../../../../App/HttpResponseErrorHandlerContext'
import { useSyncStatus } from '../../../../App/mermaidData/syncApiDataIntoOfflineStorage/SyncStatusContext'
import { useUnsavedDirtyFormDataUtilities } from '../../../../library/useUnsavedDirtyFormDataUtilities'
import CollectRecordFormPage from '../CollectRecordFormPage'
import ErrorBoundary from '../../../ErrorBoundary'
import fishbeltObservationReducer from './fishbeltObservationReducer'
import FishBeltObservationTable from './FishBeltObservationTable'
import FishBeltTransectInputs from './FishBeltTransectInputs'
import NewAttributeModal from '../../../NewAttributeModal'
import useIsMounted from '../../../../library/useIsMounted'
import { useTranslation } from 'react-i18next'

const FishBeltForm = ({ isNewRecord = true }) => {
  const { t } = useTranslation()
  const { recordId, projectId } = useParams()
  const { isSyncInProgress } = useSyncStatus()
  const isMounted = useIsMounted()

  const { databaseSwitchboardInstance } = useDatabaseSwitchboardInstance()
  const handleHttpResponseError = useHttpResponseErrorHandler()
  const observationsReducer = useReducer(fishbeltObservationReducer, [])
  const [areObservationsInputsDirty, setAreObservationsInputsDirty] = useState(false)
  const [collectRecordBeingEdited, setCollectRecordBeingEdited] = useState()
  const [fishNameConstants, setFishNameConstants] = useState([])
  const [fishNameOptions, setFishNameOptions] = useState([])
  const [idsNotAssociatedWithData, setIdsNotAssociatedWithData] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [isNewFishNameModalOpen, setIsNewFishNameModalOpen] = useState(false)
  const [modalAttributeOptions, setModalAttributeOptions] = useState([])
  const [observationIdToAddNewBenthicAttributeTo, setObservationIdToAddNewBenthicAttributeTo] =
    useState()
  const [observationsState, observationsDispatch] = observationsReducer
  const [sites, setSites] = useState([])
  const [subNavNode, setSubNavNode] = useState(null)
  const { currentUser } = useCurrentUser()
  const [fishSpecies, setFishSpecies] = useState([])
  const [fishGenera, setFishGenera] = useState([])
  const [fishFamilies, setFishFamilies] = useState([])
  const [fishGroupings, setFishGroupings] = useState([])

  const errorMessage = isNewRecord
    ? t('sample_units.errors.supporting_data_unavailable')
    : t('sample_units.errors.data_unavailable')

  const _getSupportingData = useEffect(() => {
    if (databaseSwitchboardInstance && projectId && !isSyncInProgress) {
      const promises = [
        databaseSwitchboardInstance.getSitesWithoutOfflineDeleted(projectId),
        databaseSwitchboardInstance.getFishSpecies(),
        databaseSwitchboardInstance.getFishGenera(),
        databaseSwitchboardInstance.getFishFamilies(),
        databaseSwitchboardInstance.getFishGroupings(),
        databaseSwitchboardInstance.getProject(projectId),
      ]

      if (recordId && !isNewRecord) {
        promises.push(databaseSwitchboardInstance.getCollectRecord(recordId))
      }
      Promise.all(promises)
        .then(
          async ([
            sitesResponse,
            fishSpeciesResponse,
            fishGeneraResponse,
            fishFamiliesResponse,
            fishGroupingsResponse,
            projectResponse,
            // collectRecord needs to be last in array because its pushed to the promise array conditionally
            collectRecordResponse,
          ]) => {
            if (isMounted.current) {
              let resolvedFishSpecies = fishSpeciesResponse

              if (!isNewRecord && !collectRecordResponse && recordId) {
                setIdsNotAssociatedWithData((previousState) => [...previousState, recordId])
              }
              if (!projectResponse && projectId) {
                setIdsNotAssociatedWithData((previousState) =>
                  Array.from(new Set([...previousState, projectId])),
                )
              }

              const observationAttributeIds =
                collectRecordResponse?.data?.obs_belt_fishes
                  ?.map((observation) => observation?.fish_attribute)
                  .filter(Boolean) ?? []

              if (observationAttributeIds.length) {
                await databaseSwitchboardInstance.ensureFishSpeciesLoaded(observationAttributeIds)
                resolvedFishSpecies = await databaseSwitchboardInstance.getFishSpecies()
                if (!isMounted.current) {
                  return
                }
              }

              const updateFishNameConstants = getFishNameConstants({
                species: resolvedFishSpecies,
                genera: fishGeneraResponse,
                families: fishFamiliesResponse,
                groupings: fishGroupingsResponse,
              })

              const updateFishNameOptions = getFishNameOptions({
                species: getSelectableAttributes(resolvedFishSpecies, currentUser.id),
                genera: fishGeneraResponse,
                families: fishFamiliesResponse,
                groupings: fishGroupingsResponse,
              })

              const generaOptions = fishGeneraResponse.map((genus) => ({
                label: genus.name,
                value: genus.id,
              }))

              setSubNavNode(
                getDataForSubNavNode({
                  isNewRecord,
                  collectRecord: collectRecordResponse,
                  sites: sitesResponse,
                  protocol: collectRecordResponse?.data.protocol,
                }),
              )

              setSites(sortArrayByObjectKey(sitesResponse, 'name'))
              setCollectRecordBeingEdited(collectRecordResponse)
              setFishNameConstants(updateFishNameConstants)
              setFishNameOptions(updateFishNameOptions)
              setModalAttributeOptions(generaOptions)
              setFishSpecies(resolvedFishSpecies)
              setFishGenera(fishGeneraResponse)
              setFishFamilies(fishFamiliesResponse)
              setFishGroupings(fishGroupingsResponse)
              setIsLoading(false)
            }
          },
        )
        .catch((error) => {
          handleHttpResponseError({
            error,
            callback: () => {
              toast.error(...getToastArguments(errorMessage))
            },
          })
        })
    }
  }, [
    databaseSwitchboardInstance,
    isMounted,
    isNewRecord,
    recordId,
    projectId,
    handleHttpResponseError,
    isSyncInProgress,
    errorMessage,
    currentUser,
  ])

  const closeNewObservationModal = () => {
    setIsNewFishNameModalOpen(false)
  }
  const { getPersistedUnsavedFormData: getPersistedUnsavedFormikData } =
    useUnsavedDirtyFormDataUtilities(`${currentUser.id}-unsavedSampleInfoInputs`)

  const initialFormikFormValues = useMemo(() => {
    const collectRecordInitialValues = getPersistedUnsavedFormikData() ?? {
      ...getCollectRecordDataInitialValues(collectRecordBeingEdited),
      ...getSampleInfoInitialValues(collectRecordBeingEdited),
    }

    return (
      getPersistedUnsavedFormikData() ?? {
        ...collectRecordInitialValues,
        ...getTransectInitialValues(collectRecordBeingEdited, 'fishbelt_transect'),
      }
    )
  }, [collectRecordBeingEdited, getPersistedUnsavedFormikData])

  const handleCollectRecordChange = (collectRecordResponse) => {
    setCollectRecordBeingEdited(collectRecordResponse)
    setSubNavNode(
      getDataForSubNavNode({
        isNewRecord,
        collectRecord: collectRecordResponse,
        sites,
        protocol: collectRecordResponse.data.protocol,
      }),
    )
  }

  const updateFishNameOptionsStateWithOfflineStorageData = useCallback(() => {
    if (databaseSwitchboardInstance) {
      Promise.all([
        databaseSwitchboardInstance.getFishSpecies(),
        databaseSwitchboardInstance.getFishGenera(),
        databaseSwitchboardInstance.getFishFamilies(),
      ]).then(([species, genera, families]) => {
        const updateFishNameOptions = getFishNameOptions({
          species: getSelectableAttributes(species, currentUser.id),
          genera,
          families,
        })

        setFishNameOptions(updateFishNameOptions)
      })
    }
  }, [databaseSwitchboardInstance, currentUser])

  const onSubmitNewFishSpecies = ({ genusId, genusName, speciesName }) => {
    databaseSwitchboardInstance
      .addFishSpecies({
        genusId,
        genusName,
        speciesName,
      })
      .then((newFishSpecies) => {
        observationsDispatch({
          type: 'updateFishName',
          payload: {
            observationId: observationIdToAddNewBenthicAttributeTo,
            newFishName: newFishSpecies.id,
          },
        })
        updateFishNameOptionsStateWithOfflineStorageData()
        toast.success(
          ...getToastArguments(
            t('fish_belt_observations.proposed_species_saved', {
              genusName: genusName,
              speciesName: speciesName,
            }),
          ),
        )
      })
      .catch((error) => {
        if (error.message === 'Species already exists') {
          toast.warning(
            ...getToastArguments(
              t('fish_belt_observations.errors.species_already_exists', {
                genusName: genusName,
                speciesName: speciesName,
              }),
            ),
          )

          observationsDispatch({
            type: 'updateFishName',
            payload: {
              observationId: observationIdToAddNewBenthicAttributeTo,
              newFishName: error.existingSpecies.id,
            },
          })
        } else {
          handleHttpResponseError({
            error,
            callback: () => {
              toast.error(
                ...getToastArguments(
                  t('fish_belt_observations.errors.proposed_species_unsaved', {
                    genusName: genusName,
                    speciesName: speciesName,
                  }),
                ),
              )
            },
          })
        }
      })

    return Promise.resolve()
  }

  const PartiallyAppliedFishBeltObservationsTable = useCallback(
    (props) => {
      // the conditional here makes tests happy
      if (fishNameConstants.length && fishNameOptions.length) {
        return (
          <FishBeltObservationTable
            fishNameConstants={fishNameConstants}
            fishNameOptions={fishNameOptions}
            fishFamilies={fishFamilies}
            fishGroupings={fishGroupings}
            fishGenera={fishGenera}
            fishSpecies={fishSpecies}
            {...props}
          />
        )
      }

      return null
    },

    [fishFamilies, fishGenera, fishNameConstants, fishNameOptions, fishSpecies, fishGroupings],
  )

  const PartiallyAppliedFishBeltTransectInputs = useCallback(
    (props) => {
      return (
        <FishBeltTransectInputs
          observationsState={observationsState}
          observationsDispatch={observationsDispatch}
          {...props}
        />
      )
    },
    [observationsDispatch, observationsState],
  )

  return (
    <ErrorBoundary>
      <CollectRecordFormPage
        areObservationsInputsDirty={areObservationsInputsDirty}
        collectRecordBeingEdited={collectRecordBeingEdited}
        handleCollectRecordChange={handleCollectRecordChange}
        idsNotAssociatedWithData={idsNotAssociatedWithData}
        initialFormikFormValues={initialFormikFormValues}
        isNewRecord={isNewRecord}
        isParentDataLoading={isLoading}
        observationsTable1Reducer={observationsReducer}
        ObservationTable1={PartiallyAppliedFishBeltObservationsTable}
        sampleUnitFormatSaveFunction={reformatFormValuesIntoFishBeltRecord}
        sampleUnitName="fishbelt"
        SampleUnitTransectInputs={PartiallyAppliedFishBeltTransectInputs}
        setAreObservationsInputsDirty={setAreObservationsInputsDirty}
        setIdsNotAssociatedWithData={setIdsNotAssociatedWithData}
        setIsNewBenthicAttributeModalOpen={setIsNewFishNameModalOpen}
        setObservationIdToAddNewBenthicAttributeTo={setObservationIdToAddNewBenthicAttributeTo}
        subNavNode={subNavNode}
      />
      {!!projectId && !!currentUser && (
        <NewAttributeModal
          sampleUnit="fishbelt"
          isOpen={isNewFishNameModalOpen}
          onDismiss={closeNewObservationModal}
          onSubmit={onSubmitNewFishSpecies}
          modalAttributeOptions={modalAttributeOptions}
        />
      )}
    </ErrorBoundary>
  )
}

FishBeltForm.propTypes = {
  isNewRecord: PropTypes.bool,
}

export default FishBeltForm
